import React from 'react';
import { SparklesIcon, ArrowsPointingOutIcon, ArrowsPointingInIcon } from '@heroicons/react/24/outline';

interface CanvasToolbarProps {
  historyIndex: number;
  historyLength: number;
  selectedLayerId: string | null;
  onUndo: () => void;
  onRedo: () => void;
  onMoveLayerForward: () => void;
  onMoveLayerBackward: () => void;
  onAlignLeft?: () => void;
  onAlignCenter?: () => void;
  onAlignRight?: () => void;
  onAlignTop?: () => void;
  onAlignMiddle?: () => void;
  onAlignBottom?: () => void;
  isMobile?: boolean;
  onOpenAIModal?: () => void;
  hasActiveSelection?: boolean;
}

const CanvasToolbar: React.FC<CanvasToolbarProps> = ({
  historyIndex,
  historyLength,
  selectedLayerId,
  onUndo,
  onRedo,
  onMoveLayerForward,
  onMoveLayerBackward,
  onAlignLeft,
  onAlignCenter,
  onAlignRight,
  onAlignTop,
  onAlignMiddle,
  onAlignBottom,
  isMobile = false,
  onOpenAIModal,
  hasActiveSelection = false
}) => {
  const [showAlignMenu, setShowAlignMenu] = React.useState(false);

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (showAlignMenu && !target.closest('.align-dropdown')) {
        setShowAlignMenu(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showAlignMenu]);
  return (
    <div className="bg-gray-100 px-4 py-2 flex items-center space-x-4 border-b">
      {/* Undo Button */}
      <button 
        className={`p-2 rounded relative group ${
          historyIndex > 0 ? 'bg-gray-200 hover:bg-gray-300' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
        title="Undo"
        onClick={() => { window.dispatchEvent(new CustomEvent('giftmkr:undo')); onUndo(); }}
        disabled={historyIndex <= 0}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
        </svg>
      </button>

      {/* Redo Button */}
      <button 
        className={`p-2 rounded relative group ${
          historyIndex < historyLength - 1 ? 'bg-gray-200 hover:bg-gray-300' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
        title="Redo"
        onClick={() => { window.dispatchEvent(new CustomEvent('giftmkr:redo')); onRedo(); }}
        disabled={historyIndex >= historyLength - 1}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 10h-10a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
        </svg>
      </button>

      {/* Forward Button */}
      <button 
        className={`p-2 rounded relative group ${
          (selectedLayerId || hasActiveSelection) ? 'bg-gray-200 hover:bg-gray-300' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
        title="Forward (Bring to Front)"
        onClick={() => { window.dispatchEvent(new CustomEvent('giftmkr:bringToFront')); onMoveLayerForward(); }}
        disabled={!(selectedLayerId || hasActiveSelection)}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
        </svg>
      </button>

      {/* Backward Button */}
      <button 
        className={`p-2 rounded relative group ${
          (selectedLayerId || hasActiveSelection) ? 'bg-gray-200 hover:bg-gray-300' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
        }`}
        title="Backward (Send to Back)"
        onClick={() => { window.dispatchEvent(new CustomEvent('giftmkr:sendToBack'));  }}
        disabled={!(selectedLayerId || hasActiveSelection)}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

     

      {/* Align Button */}
      <div className="relative align-dropdown">
      <button 
          className={`p-2 rounded relative group ${
            (selectedLayerId || hasActiveSelection) 
              ? showAlignMenu 
                ? 'bg-blue-200 hover:bg-blue-300' 
                : 'bg-gray-200 hover:bg-gray-300'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
          }`}
        title="Align"
          onClick={(e) => {
            e.stopPropagation();
            if (selectedLayerId || hasActiveSelection) {
              console.log('Align button clicked, current state:', showAlignMenu);
              setShowAlignMenu(!showAlignMenu);
            }
          }}
          disabled={!(selectedLayerId || hasActiveSelection)}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>
        
        {/* Alignment Dropdown Menu */}
        {showAlignMenu && (selectedLayerId || hasActiveSelection) && (
          <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-[9999] min-w-32 align-dropdown">
            {/* Horizontal Alignment */}
            <div className="px-3 py-2 border-b border-gray-200">
              <div className="text-xs font-medium text-gray-600 mb-2">Horizontal</div>
              <div className="flex space-x-2">
                <button
                  className="p-2 bg-gray-100 rounded hover:bg-gray-200"
                  title="Align Left"
                  onClick={() => {
                    onAlignLeft?.();
                    setShowAlignMenu(false);
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h8M4 12h8M4 18h8" />
                  </svg>
                </button>
                <button
                  className="p-2 bg-gray-100 rounded hover:bg-gray-200"
                  title="Align Center"
                  onClick={() => {
                    onAlignCenter?.();
                    setShowAlignMenu(false);
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 6h8M8 12h8M8 18h8" />
                  </svg>
                </button>
                <button
                  className="p-2 bg-gray-100 rounded hover:bg-gray-200"
                  title="Align Right"
                  onClick={() => {
                    onAlignRight?.();
                    setShowAlignMenu(false);
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6h8M12 12h8M12 18h8" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Vertical Alignment */}
            <div className="px-3 py-2">
              <div className="text-xs font-medium text-gray-600 mb-2">Vertical</div>
              <div className="flex space-x-2">
                <button
                  className="p-2 bg-gray-100 rounded hover:bg-gray-200"
                  title="Align Top"
                  onClick={() => {
                    onAlignTop?.();
                    setShowAlignMenu(false);
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 4v16M12 4v16M18 4v16" />
                  </svg>
                </button>
                <button
                  className="p-2 bg-gray-100 rounded hover:bg-gray-200"
                  title="Align Middle"
                  onClick={() => {
                    onAlignMiddle?.();
                    setShowAlignMenu(false);
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 8v8M12 8v8M18 8v8" />
                  </svg>
                </button>
                <button
                  className="p-2 bg-gray-100 rounded hover:bg-gray-200"
                  title="Align Bottom"
                  onClick={() => {
                    onAlignBottom?.();
                    setShowAlignMenu(false);
                  }}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 12v8M12 12v8M18 12v8" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Guidelines Button */}
      {/* <button 
        className="p-2 bg-gray-200 rounded hover:bg-gray-300 relative group"
        title="Guidelines"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        </svg>
      </button> */}
 

            {/* AI Button - Only show on mobile */}
      {isMobile && (
      <button 
        className="p-2 bg-gray-200 rounded hover:bg-gray-300 relative group"
          title="AI Enhancement"
          onClick={onOpenAIModal}
      >
          <SparklesIcon className="w-5 h-5 text-purple-600" />
      </button>
      )}
    </div>
  );
};

export default CanvasToolbar; 
